import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { baseUrl } from "../../config/baseUrl";

import { toast } from "react-toastify";

export default function LibrarySlots() {
  const navigate = useNavigate();


  // --- STATES ---
  const [tenureList, setTenureList] = useState([]);
  const [slots, setSlots] = useState([]); 
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("create");
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedMergeSlots, setSelectedMergeSlots] = useState([]); // Stores objects of selected slots

  const [libraryInfo, setLibraryInfo] = useState({ id: "", name: "", location: "" });

  const [newSlot, setNewSlot] = useState({
    slot_name: "",
    libraryPrice: "",
    numberOfSeats: "",
    tenure: "", 
    fromTimeHours: "1:00",
    fromTimestamp: "AM",
    toTimeHours: "1:00",
    toTimestamp: "AM"
  });

  // --- FETCH DATA ---
  const fetchSlots = async (libraryId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${baseUrl}/owner/slot?library=${libraryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.status) {
        setSlots(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch slots:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const tenureRes = await fetch(`${baseUrl}/library-tenure?status=active`, { headers });
        const tenureData = await tenureRes.json();
        if (tenureData.status && tenureData.data) setTenureList(tenureData.data);

        const libRes = await fetch(`${baseUrl}/owner/library/details`, { headers });
        const libData = await libRes.json();

        if (libRes.status === 401) { navigate("/login"); return; }

        if (libData.status && libData.data) {
          const library = libData.data;
          setLibraryInfo({
            id: library._id,
            name: library.libraryName || "My Library",
            location: `${library.area || ""}, ${library.city || ""}`,
          });
          fetchSlots(library._id);
        }
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
      }
    };
    fetchData();
  }, [navigate]);

  const handleSaveSlot = async () => {
    const token = localStorage.getItem("token");
    
    let payload = {
      library: libraryInfo.id, 
      slot_name: newSlot.slot_name,
      libraryPrice: Number(newSlot.libraryPrice),
      numberOfSeats: Number(newSlot.numberOfSeats),
      tenure: newSlot.tenure,
      fromTimeHours: newSlot.fromTimeHours,
      fromTimestamp: newSlot.fromTimestamp,
      toTimeHours: newSlot.toTimeHours,
      toTimestamp: newSlot.toTimestamp
    };

    if (activeTab === "merge") {
      payload = {
        slotIds: selectedMergeSlots.map(s => s._id),
        libraryPrice: Number(newSlot.libraryPrice),
        numberOfSeats: Number(newSlot.numberOfSeats),
      };
    }

    const url = isEditing 
      ? `${baseUrl}/owner/slot/update/${editId}` 
      : (activeTab === "merge" ? `${baseUrl}/owner/slot/mearge` : `${baseUrl}/owner/slot`);
    
    const method = isEditing ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.status) {
        toast.success(activeTab === "merge" ? "Slots merged successfully!" : (isEditing ? "Slot updated successfully!" : "Slot saved successfully!"));
        setShowModal(false);
        resetForm();
        fetchSlots(libraryInfo.id); 
      } else {
        toast.error(data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Save slot error:", error);
      toast.error("An error occurred while saving the slot");
    }
  };

  // --- HANDLERS ---
  const resetForm = () => {
    setEditId(null);
    setSelectedMergeSlots([]);
    setNewSlot({
        slot_name: "", libraryPrice: "", numberOfSeats: "", tenure: "",
        fromTimeHours: "1:00", fromTimestamp: "AM", toTimeHours: "1:00", toTimestamp: "AM"
    });
  };

  const handleMergeSelect = (e) => {
    const slotId = e.target.value;
    if (!slotId) return;
    
    const slotObj = slots.find(s => s._id === slotId);
    if (!slotObj) return;

    if (selectedMergeSlots.length > 0) {
      const firstSlot = selectedMergeSlots[0];
      const lastSlot = selectedMergeSlots[selectedMergeSlots.length - 1];

      if (slotObj.tenure?._id !== firstSlot.tenure?._id) {
        toast.error("Selection Error: Please select slots with the same Tenure only.");
        e.target.value = "";
        return;
      }

      // const lastEndTime = `${lastSlot.toTimeHours} ${lastSlot.toTimestamp}`;
      // const currentStartTime = `${slotObj.fromTimeHours} ${slotObj.fromTimestamp}`;
      // if (lastEndTime !== currentStartTime) {
      //   toast.error(`Sequence Error: This slot must start at ${lastEndTime} to be in sequence.`);
      //   e.target.value = "";
      //   return;
      // }
    }

    if (!selectedMergeSlots.some(s => s._id === slotId)) {
      const updatedList = [...selectedMergeSlots, slotObj];
      setSelectedMergeSlots(updatedList);
      
      if (updatedList.length === 1) {
        setNewSlot(prev => ({ ...prev, tenure: slotObj.tenure?._id }));
      }
    }
    e.target.value = ""; 
  };

  const handleDeleteSlot = async (id) => {
    if (!window.confirm("Are you sure you want to delete this slot?")) return;

    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${baseUrl}/owner/slot/delete/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.status) {
        toast.success("Slot deleted successfully!");
        fetchSlots(libraryInfo.id); 
      } else {
        toast.error(data.message || "Failed to delete slot");
      }
    } catch (error) {
      console.error("Delete slot error:", error);
      toast.error("Server error while deleting");
    }
  };

  const removeSelectedSlot = (id) => {
    setSelectedMergeSlots(selectedMergeSlots.filter(s => s._id !== id));
  };

  const handleOpenAddModal = () => {
    setIsEditing(false);
    resetForm();
    setActiveTab("create");
    setShowModal(true);
  };

  const handleOpenEditModal = (slot) => {
    setIsEditing(true);
    setEditId(slot._id);
    setActiveTab("create");
    setNewSlot({
        slot_name: slot.slot_name,
        libraryPrice: slot.libraryPrice,
        numberOfSeats: slot.numberOfSeats,
        tenure: slot.tenure?._id || "",
        fromTimeHours: slot.fromTimeHours,
        fromTimestamp: slot.fromTimestamp,
        toTimeHours: slot.toTimeHours,
        toTimestamp: slot.toTimestamp
    });
    setShowModal(true);
  };

  const timeOptions = [];
  for (let i = 1; i <= 12; i++) {
    timeOptions.push(`${i}:00`);
    timeOptions.push(`${i}:30`);
  }

  return (
    <section className="main-sec" style={{ background: "#f8fafc", minHeight: "100vh", fontFamily: 'sans-serif' }}>
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h4 className="fw-bold m-0" style={{ color: "#2d3748" }}>Library Slots</h4>
         <div className="col-lg-4 d-flex justify-content-lg-end">
            <Link to="/library" className="btn btn-outline-primary mt-2 mt-lg-0">
              Back to My Library
            </Link>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center shadow-sm p-4 mb-4 bg-white rounded-3 border mt-4">
          <div>
            <h6 className="fw-bold m-0 mb-1">{libraryInfo.name || "Loading..."}</h6>
            <p className="text-muted m-0 small">{libraryInfo.location || "Fetching location..."}</p>
          </div>
          <button className="btn btn-emerald" onClick={handleOpenAddModal}>
            <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Slot
          </button>
        </div>

        {slots.length > 0 ? slots.map((slot) => (
          <div key={slot._id} className="shadow-sm mb-3 bg-white border rounded-3 overflow-hidden">
            <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom bg-light">
              <h6 className="fw-bold m-0 text-dark">{slot.slot_name}</h6>
              <button 
                className="btn-delete" 
                onClick={() => handleDeleteSlot(slot._id)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
            <div className="slot-grid px-4 py-3">
              <div className="input-group-custom"><label>Rs Price *</label><input type="text" value={slot.libraryPrice} readOnly /></div>
              <div className="input-group-custom"><label>Seats *</label><input type="text" value={`${slot.remainingSeats}/${slot.numberOfSeats}`} readOnly /></div>
              <div className="input-group-custom"><label>Tenure *</label><input type="text" value={slot.tenure?.tenure || "N/A"} readOnly /></div>
              <div className="input-group-custom"><label>From *</label><input type="text" value={`${slot.fromTimeHours} ${slot.fromTimestamp}`} readOnly /></div>
              <div className="input-group-custom"><label>To *</label><input type="text" value={`${slot.toTimeHours} ${slot.toTimestamp}`} readOnly /></div>
              <div className="input-group-custom d-flex align-items-end gap-3">
                <div className="flex-grow-1"><label>Merge Status</label><input type="text" value={slot.is_mearge ? "Yes" : "No"} readOnly /></div>
                <button className="btn-edit-inline" onClick={() => handleOpenEditModal(slot)}><FontAwesomeIcon icon={faEdit} /></button>
              </div>
            </div>
          </div>
        )) : <div className="text-center p-5 text-muted bg-white rounded-3 border shadow-sm">No slots found.</div>}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box shadow-lg">
            <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            
            {!isEditing && (
              <div className="d-flex justify-content-center mb-4">
                <div className="tab-container border rounded-2 d-flex">
                  <button className={`tab-btn ${activeTab === 'merge' ? 'active' : ''}`} onClick={() => setActiveTab('merge')}>Merge Existing</button>
                  <button className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`} onClick={() => setActiveTab('create')}>+ Create New</button>
                </div>
              </div>
            )}

            <h5 className="fw-bold mb-4">{isEditing ? `Edit Slot` : (activeTab === "create" ? "Create New Slot" : "Merge Existing Slot")}</h5>

            <div className="modal-body-grid">
              {activeTab === "create" ? (
                <>
                  <div className="input-group-custom"><label>Slot Name *</label><input type="text" placeholder="e.g., Slot A" value={newSlot.slot_name} onChange={(e) => setNewSlot({...newSlot, slot_name: e.target.value})} /></div>
                  <div className="input-group-custom"><label>Rs Price *</label><input type="number" placeholder="Enter price" value={newSlot.libraryPrice} onChange={(e) => setNewSlot({...newSlot, libraryPrice: e.target.value})} /></div>
                  <div className="input-group-custom"><label>Tenure *</label>
                    <select className="form-select" value={newSlot.tenure} onChange={(e) => setNewSlot({...newSlot, tenure: e.target.value})}>
                        <option value="">Select Tenure</option>
                        {tenureList.map(t => <option key={t._id} value={t._id}>{t.tenure}</option>)}
                    </select>
                  </div>
                  <div className="input-group-custom"><label>Seats *</label><input type="number" placeholder="e.g., 100" value={newSlot.numberOfSeats} onChange={(e) => setNewSlot({...newSlot, numberOfSeats: e.target.value})} /></div>
                  <div className="input-group-custom">
                    <label>From Time *</label>
                    <div className="d-flex gap-1">
                        <select className="form-select flex-grow-1" value={newSlot.fromTimeHours} onChange={(e) => setNewSlot({...newSlot, fromTimeHours: e.target.value})}>{timeOptions.map(t => <option key={t} value={t}>{t}</option>)}</select>
                        <select className="form-select" style={{ width: '75px' }} value={newSlot.fromTimestamp} onChange={(e) => setNewSlot({...newSlot, fromTimestamp: e.target.value})}><option>AM</option><option>PM</option></select>
                    </div>
                  </div>
                  <div className="input-group-custom">
                    <label>To Time *</label>
                    <div className="d-flex gap-1">
                        <select className="form-select flex-grow-1" value={newSlot.toTimeHours} onChange={(e) => setNewSlot({...newSlot, toTimeHours: e.target.value})}>{timeOptions.map(t => <option key={t} value={t}>{t}</option>)}</select>
                        <select className="form-select" style={{ width: '75px' }} value={newSlot.toTimestamp} onChange={(e) => setNewSlot({...newSlot, toTimestamp: e.target.value})}><option>AM</option><option>PM</option></select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="input-group-custom full-width">
                    <label>Slot Name *</label>
                    <div className="merge-selection-container border rounded-2 bg-white d-flex align-items-center">
                      <div className="dropdown-part border-end">
                        <select className="border-0 px-2 py-2" style={{ outline: "none", width: '100%', background: 'transparent' }} onChange={handleMergeSelect}>
                          <option value="">Select slots</option>
                          {slots.map(s => <option key={s._id} value={s._id}>{s.slot_name}</option>)}
                        </select>
                      </div>
                      <div className="chips-part flex-grow-1 d-flex flex-wrap gap-2 p-2">
                        {selectedMergeSlots.length === 0 && <span className="text-muted small">Select slots to merge</span>}
                        {selectedMergeSlots.map(s => (
                          <div key={s._id} className="tag-badge">
                            {s.slot_name}
                            <span className="ms-2 cursor-pointer remove-tag" onClick={() => removeSelectedSlot(s._id)}>&times;</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="input-group-custom">
                    <label>Tenure (Auto-Selected) *</label>
                    <select 
                      className="form-select" 
                      value={newSlot.tenure} 
                      disabled={activeTab === "merge" && selectedMergeSlots.length > 0} 
                      onChange={(e) => setNewSlot({...newSlot, tenure: e.target.value})}
                    >
                      <option value="">Select Tenure</option>
                      {tenureList.map(t => <option key={t._id} value={t._id}>{t.tenure}</option>)}
                    </select>
                  </div>
                  <div className="input-group-custom"><label>Rs Price *</label><input type="number" placeholder="Enter price" value={newSlot.libraryPrice} onChange={(e) => setNewSlot({...newSlot, libraryPrice: e.target.value})} /></div>
                  <div className="input-group-custom full-width"><label>Seats *</label><input type="number" placeholder="e.g., 100" value={newSlot.numberOfSeats} onChange={(e) => setNewSlot({...newSlot, numberOfSeats: e.target.value})} /></div>
                </>
              )}
            </div>

            <div className="d-flex justify-content-center gap-3 mt-5">
              <button className="btn btn-light border px-5" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-emerald px-5" onClick={handleSaveSlot}>{isEditing ? "Update Slot" : "+ Add Slot"}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .btn-emerald { background-color: #38a189; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; }
        .btn-delete { color: #ef4444; border: none; background: none; font-size: 18px; cursor: pointer; }
        .btn-edit-inline { border: none; background: none; color: #38a189; font-size: 20px; cursor: pointer; }
        .slot-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 15px; }
        .input-group-custom label { display: block; font-size: 13px; font-weight: 600; color: #4b5563; margin-bottom: 5px; }
        .input-group-custom input, .input-group-custom select { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; background: #f9fafb; }
        .modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-box { background: white; padding: 35px; border-radius: 15px; width: 650px; position: relative; }
        .close-btn { position: absolute; right: 20px; top: 20px; border: none; background: none; font-size: 25px; color: #9ca3af; cursor: pointer; }
        .tab-container { background: #f3f4f6; padding: 4px; }
        .tab-btn { border: none; padding: 8px 20px; border-radius: 5px; background: none; color: #6b7280; font-weight: 600; cursor: pointer; }
        .tab-btn.active { background: #38a189; color: white; }
        .modal-body-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
        .full-width { grid-column: span 2; }
        .merge-selection-container { border: 1px solid #d1d5db; min-height: 48px; display: flex; overflow: hidden; }
        .dropdown-part { width: 140px; background: #f9fafb; display: flex; align-items: center; }
        .chips-part { flex-grow: 1; background: #fff; min-height: 40px; }
        .tag-badge { background: #e6fffa; color: #0694a2; border: 1px solid #b2f5ea; padding: 4px 12px; border-radius: 50px; font-size: 13px; font-weight: 500; display: flex; align-items: center; }
        .remove-tag { font-size: 18px; line-height: 1; color: #0694a2; font-weight: bold; }
        .remove-tag:hover { color: #c53030; }
        .cursor-pointer { cursor: pointer; }
      `}</style>
    </section>
  );
}














// "use client";

// import { useEffect, useMemo, useRef, useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//     faPlus,
//     faTrash,
//     faEdit,
//     faSpinner,
//     faTimes,
// } from "@fortawesome/free-solid-svg-icons";
// import { baseUrl } from "../../config/baseUrl";

// // Define createEmptySlot before LibrarySlots component
// const createEmptySlot = () => ({
//     libraryPrice: "",
//     numberOfSeats: "",
//     remainingSeats: "",
//     tenure: "",
//     fromTimeHours: "",
//     fromTimestamp: "AM",
//     toTimeHours: "",
//     toTimestamp: "AM",
// });

// export default function LibrarySlots() {
//     const navigate = useNavigate();
//     const addedSlotIndexRef = useRef(null);

//     // States from baseline
//     const [fetchLoading, setFetchLoading] = useState(true);
//     const [saving, setSaving] = useState(false);
//     const [tenureList, setTenureList] = useState([]);
//     const [slots, setSlots] = useState([createEmptySlot()]);
//     const [libraryMeta, setLibraryMeta] = useState({ libraryName: "", city: "", area: "" });
//     const [libraryPayload, setLibraryPayload] = useState({});

//     // States for Modal and UI
//     const [showModal, setShowModal] = useState(false);
//     const [activeTab, setActiveTab] = useState("create");
//     // Merge slots state for Tag UI
//     const [selectedMergeSlots, setSelectedMergeSlots] = useState([]);

//     useEffect(() => {
//         initializePage();
//     }, []);

//     const initializePage = async () => {
//         try {
//             setFetchLoading(true);
//             await Promise.all([fetchLibraryData(), fetchTenureData()]);
//         } catch (error) {
//             toast.error(error.message || "Failed to load data");
//         } finally {
//             setFetchLoading(false);
//         }
//     };

//     const fetchTenureData = async () => {
//         const response = await fetch(`${baseUrl}/library-tenure?status=active`, {
//             headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
//         });
//         const data = await response.json();
//         if (data.status) setTenureList(data.data);
//     };

//     const fetchLibraryData = async () => {
//         const response = await fetch(`${baseUrl}/owner/library/details`, {
//             headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
//         });
//         const data = await response.json();
//         if (data.status && data.data) {
//             const library = data.data;
//             setLibraryMeta({
//                 libraryName: library.libraryName || "My Library",
//                 city: library.city || "",
//                 area: library.area || "",
//             });
//             setLibraryPayload(library);
//             setSlots(library.slots?.length > 0 ? library.slots : [createEmptySlot()]);
//         }
//     };

//     // Merge Selection Logic
//     const handleMergeSelect = (e) => {
//         const slotId = e.target.value;
//         if (!slotId) return;

//         // Check if slot is already selected
//         const alreadySelected = selectedMergeSlots.some((s) => s.slotId === slotId);
//         if (!alreadySelected) {
//             const slotToAdd = slots.find((s) => s.slotId === slotId);
//             if (slotToAdd) {
//                 setSelectedMergeSlots([...selectedMergeSlots, slotToAdd]);
//             }
//         }
//         // Reset dropdown
//         e.target.value = "";
//     };

//     const removeMergeTag = (slotId) => {
//         setSelectedMergeSlots(
//             selectedMergeSlots.filter((slot) => slot.slotId !== slotId)
//         );
//     };

//     const handleSubmit = async () => {
//         // Add your validation and merge/create logic from baseline here
//         // Then call the PUT API to save
//         toast.info("API save logic needs integration here");
//         setShowModal(false);
//     };

//     if (fetchLoading) {
//         return (
//             <div className="loader-container">
//                 <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#38a189" />
//             </div>
//         );
//     }

//     return (
//         <section className="main-sec">
//             <div className="container-fluid p-4">
//                 {/* Header from Image 1 */}
//                 <div className="d-flex justify-content-between align-items-center">
//                     <h4 className="fw-bold m-0" style={{ color: "#2d3748" }}>
//                         Library Slots
//                     </h4>
//                     <Link
//                         to="/library"
//                         className="text-decoration-none"
//                         style={{ color: "#3182ce", fontSize: "14px" }}
//                     >
//                         Back to My Library
//                     </Link>
//                 </div>
//                 <nav className="mb-4">
//                     <small className="text-muted">
//                         Dashboard &gt; My Library &gt; Library Slots
//                     </small>
//                 </nav>

//                 {/* Library Info Card from Image 1 */}
//                 <div className="library-card d-flex justify-content-between align-items-center shadow-sm p-4 mb-4 bg-white rounded-3 border">
//                     <div>
//                         <h6 className="fw-bold m-0 mb-1">{libraryMeta.libraryName}</h6>
//                         <p className="text-muted m-0 small">
//                             {libraryMeta.area}, {libraryMeta.city}
//                         </p>
//                     </div>
//                     <button
//                         className="btn btn-emerald"
//                         onClick={() => {
//                             setShowModal(true);
//                             setSelectedMergeSlots([]); // Clear tags when opening modal
//                         }}
//                     >
//                         <FontAwesomeIcon icon={faPlus} /> Add Slot
//                     </button>
//                 </div>

//                 {/* Slots List from Image 1 */}
//                 {slots.map((slot, index) => (
//                     <div
//                         key={index}
//                         className="slot-card shadow-sm mb-3 bg-white border rounded-3 overflow-hidden"
//                     >
//                         <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom bg-light">
//                             <h6 className="fw-bold m-0 text-dark">
//                                 Slot {String.fromCharCode(65 + index)}
//                             </h6>
//                             <button
//                                 className="btn-delete"
//                                 onClick={() =>
//                                     setSlots(slots.filter((_, i) => i !== index))
//                                 }
//                             >
//                                 <FontAwesomeIcon icon={faTrash} />
//                             </button>
//                         </div>

//                         <div className="slot-grid px-4 py-3">
//                             <div className="input-group-custom">
//                                 <label>Rs Price *</label>
//                                 <input type="text" value={slot.libraryPrice} readOnly />
//                             </div>
//                             <div className="input-group-custom">
//                                 <label>Seats *</label>
//                                 <input
//                                     type="text"
//                                     value={`${slot.numberOfSeats}/${slot.numberOfSeats}`}
//                                     readOnly
//                                 />
//                             </div>
//                             <div className="input-group-custom">
//                                 <label>Tenure *</label>
//                                 <input type="text" value={slot.tenure} readOnly />
//                             </div>
//                             <div className="input-group-custom">
//                                 <label>From *</label>
//                                 <input type="text" value={slot.fromTimeHours} readOnly />
//                             </div>
//                             <div className="input-group-custom">
//                                 <label>To *</label>
//                                 <input type="text" value={slot.toTimeHours} readOnly />
//                             </div>
//                             <div className="input-group-custom d-flex align-items-end gap-3">
//                                 <div className="flex-grow-1">
//                                     <label>Students *</label>
//                                     <input type="text" value={slot.remainingSeats} readOnly />
//                                 </div>
//                                 <button className="btn-edit-inline">
//                                     <FontAwesomeIcon icon={faEdit} />
//                                 </button>
//                             </div>
//                         </div>
//                     </div>
//                 ))}
//             </div>

//             {/* MODAL - Fixed Merge UI (Image 3, 4, 5) */}
//             {showModal && (
//                 <div className="modal-overlay">
//                     <div className="modal-box shadow-lg">
//                         <div className="d-flex justify-content-center mb-4 position-relative">
//                             <div className="tab-container border rounded-2 d-flex">
//                                 <button
//                                     className={`tab-btn ${activeTab === "merge" ? "active" : ""
//                                         }`}
//                                     onClick={() => setActiveTab("merge")}
//                                 >
//                                     Merge Existing
//                                 </button>
//                                 <button
//                                     className={`tab-btn ${activeTab === "create" ? "active" : ""
//                                         }`}
//                                     onClick={() => setActiveTab("create")}
//                                 >
//                                     + Create New
//                                 </button>
//                             </div>
//                             <button
//                                 className="close-btn"
//                                 onClick={() => setShowModal(false)}
//                             >
//                                 &times;
//                             </button>
//                         </div>

//                         <h5 className="fw-bold mb-4">
//                             {activeTab === "create"
//                                 ? "Create New Slot"
//                                 : "Merge Existing Slot"}
//                         </h5>

//                         <div className="modal-body-grid">
//                             {activeTab === "create" ? (
//                                 // Create New Tab UI (Image 2)
//                                 <>
//                                     <div className="input-group-custom">
//                                         <label>Slot Name *</label>
//                                         <input
//                                             type="text"
//                                             placeholder="Enter slot name (e.g., Slot A)"
//                                         />
//                                     </div>
//                                     <div className="input-group-custom">
//                                         <label>Rs Price *</label>
//                                         <input type="text" placeholder="Enter price" />
//                                     </div>
//                                     <div className="input-group-custom">
//                                         <label>Tenure *</label>
//                                         <select>
//                                             {tenureList.map((t) => (
//                                                 <option key={t._id}>{t.tenureName}</option>
//                                             ))}
//                                         </select>
//                                     </div>
//                                     <div className="input-group-custom">
//                                         <label>Seats *</label>
//                                         <input type="text" placeholder="e.g., 70/100" />
//                                     </div>
//                                     <div className="input-group-custom">
//                                         <label>From Time *</label>
//                                         <select>
//                                             <option>Select time</option>
//                                         </select>
//                                     </div>
//                                     <div className="input-group-custom">
//                                         <label>To Time *</label>
//                                         <select>
//                                             <option>Select time</option>
//                                         </select>
//                                     </div>
//                                 </>
//                             ) : (
//                                 // Merge Tab UI (Image 3, 4, 5)
//                                 <>
//                                     <div className="input-group-custom full-width">
//                                         <label>Slot Name *</label>
//                                         <div className="merge-select-container border rounded-2 bg-white d-flex align-items-center">
//                                             <select
//                                                 className="flex-grow-1 border-0 bg-transparent px-3 py-2"
//                                                 style={{ outline: "none" }}
//                                                 onChange={handleMergeSelect} // The Fix
//                                             >
//                                                 <option value="">
//                                                     Enter slot name (e.g., Slot A)
//                                                 </option>
//                                                 {slots
//                                                     .filter((s) => s.slotId)
//                                                     .map((s, i) => (
//                                                         <option key={i} value={s.slotId}>
//                                                             {`Slot ${String.fromCharCode(65 + i)}`}
//                                                         </option>
//                                                     ))}
//                                             </select>
//                                             {/* Tag Display Area */}
//                                             <div className="d-flex gap-2 p-1 pe-2">
//                                                 {selectedMergeSlots.map((slot, i) => (
//                                                     <span key={i} className="slot-tag">
//                                                         Slot{" "}
//                                                         {String.fromCharCode(65 + slots.indexOf(slot))}
//                                                         <FontAwesomeIcon
//                                                             icon={faTimes}
//                                                             className="ms-1 cursor-pointer"
//                                                             onClick={() => removeMergeTag(slot.slotId)}
//                                                         />
//                                                     </span>
//                                                 ))}
//                                             </div>
//                                         </div>
//                                     </div>
//                                     <div className="input-group-custom">
//                                         <label>Tenure *</label>
//                                         <select>
//                                             {tenureList.map((t) => (
//                                                 <option key={t._id}>{t.tenureName}</option>
//                                             ))}
//                                         </select>
//                                     </div>
//                                     <div className="input-group-custom">
//                                         <label>Rs Price *</label>
//                                         <input type="text" placeholder="Enter price" />
//                                     </div>
//                                     <div className="input-group-custom full-width">
//                                         <label>Seats *</label>
//                                         <input type="text" placeholder="e.g., 70/100" />
//                                     </div>
//                                 </>
//                             )}
//                         </div>

//                         <div className="d-flex justify-content-center gap-3 mt-4">
//                             <button
//                                 className="btn btn-light border px-4"
//                                 onClick={() => setShowModal(false)}
//                             >
//                                 Cancel
//                             </button>
//                             <button className="btn btn-emerald px-4" onClick={handleSubmit}>
//                                 {saving ? (
//                                     <FontAwesomeIcon icon={faSpinner} spin />
//                                 ) : (
//                                     "+ Add Slot"
//                                 )}
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* Styles adapted from Emerald UI */}
//             <style>{`
//         .main-sec { background-color: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
//         .loader-container { height: 100vh; display: flex; justify-content: center; align-items: center; }
        
//         .btn-emerald { background-color: #38a189; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; transition: background 0.2s; }
//         .btn-emerald:hover { background-color: #2d826e; color: white; }
        
//         .btn-delete { color: #ef4444; border: none; background: none; font-size: 18px; cursor: pointer; transition: transform 0.2s; }
//         .btn-delete:hover { transform: scale(1.1); }
//         .btn-edit-inline { border: none; background: none; color: #38a189; font-size: 20px; cursor: pointer; }

//         .slot-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 15px; }
        
//         .input-group-custom label { display: block; font-size: 13px; font-weight: 600; color: #4b5563; margin-bottom: 5px; }
//         .input-group-custom input, .input-group-custom select { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; background: white; }
//         .input-group-custom input[readonly] { background-color: #f9fafb; color: #6b7280; border-color: #e5e7eb; }

//         .modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
//         .modal-box { background: white; padding: 35px; border-radius: 15px; width: 650px; position: relative; }
        
//         .close-btn { position: absolute; right: 20px; top: 20px; background: none; border: none; font-size: 25px; color: #9ca3af; cursor: pointer; }
//         .close-btn:hover { color: #4b5563; }

//         .tab-container { background: #f3f4f6; padding: 4px; }
//         .tab-btn { border: none; padding: 8px 20px; border-radius: 5px; background: none; color: #6b7280; font-weight: 600; cursor: pointer; }
//         .tab-btn.active { background: #38a189; color: white; }
        
//         .modal-body-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
//         .full-width { grid-column: span 2; }

//         /* Tag UI specific styles */
//         .merge-select-container { border: 1px solid #d1d5db; }
//         .slot-tag { background: #f3f4f6; border: 1px solid #d1d5db; padding: 2px 8px; border-radius: 15px; font-size: 12px; display: flex; align-items: center; color: #374151; }
//         .cursor-pointer { cursor: pointer; }
//       `}</style>
//         </section>
//     );
// }